import { Controller } from '@hotwired/stimulus'

// Shows the content only once every React field in its field targets has rendered, so a form is never usable
// half-built. WIP::ReactFormComponent renders the element and targets: content hidden and the spinner visible; if the
// fields haven't all rendered within the timeout, shows the error state instead. Submit targets, rendered disabled,
// are enabled only while the content is shown.
export default class extends Controller {
  static targets = ['content', 'loading', 'error', 'submit', 'field']
  static values = { timeout: { type: Number, default: 6000 } }

  connect() {
    // Turns off the stylesheet's no-JS fallback, which shows the error after a delay if this controller never runs.
    this.element.classList.add('react-fields-gate--connected')
    this.observer = new MutationObserver(this.update)
    this.observer.observe(this.element, { childList: true, subtree: true })
    this.update()
  }

  disconnect() {
    this.observer.disconnect()
    clearTimeout(this.timer)
  }

  update = () => {
    if (this.unrenderedFields().length === 0) {
      clearTimeout(this.timer)
      this.timer = null
      this.failed = false
      this.show('content')
    } else if (this.failed) {
      this.show('error')
    } else if (!this.timer) {
      this.show('loading')
      this.timer = setTimeout(this.fail, this.timeoutValue)
    }
  }

  fail = () => {
    this.timer = null
    const unrendered = this.unrenderedFields()
    if (unrendered.length === 0) return

    this.failed = true
    this.show('error')
    window.Honeybadger?.notify('React fields did not render', {
      context: { components: unrendered.map(container => container.dataset.reactClass), url: window.location.href }
    })
  }

  unrenderedFields() {
    return this.fieldTargets
      .flatMap(field => Array.from(field.querySelectorAll('[data-react-class]')))
      .filter(container => !container.firstElementChild)
  }

  // Acts only on a change of state, so other changes inside the form (e.g. a Galette button showing its loading
  // label while submitting) don't re-enable a submit button it has disabled.
  show(state) {
    if (state === this.state) return
    this.state = state

    this.contentTargets.forEach(target => target.classList.toggle('d-none', state !== 'content'))
    this.loadingTargets.forEach(target => target.classList.toggle('d-none', state !== 'loading'))
    this.errorTargets.forEach(target => target.classList.toggle('d-none', state !== 'error'))
    this.submitTargets.forEach(target => {
      target.disabled = state !== 'content'
      // Galette buttons keep their disabled styling in step through these events.
      target.dispatchEvent(new CustomEvent(state === 'content' ? 'galette:enable' : 'galette:disable'))
    })
  }
}
